/**
 * Figuras decorativas del sistema.
 *
 * Son SVG propios y no iconos de librería porque las formas de lucide son de
 * trazo fino y uniforme: puestas en grande sobre el degradado se ven como una
 * hoja de iconos, no como una ilustración. Estas van en silueta rellena, que
 * es lo que aguanta el tamaño y da el aire de peluquería canina.
 *
 * Todas son `aria-hidden`: no aportan información, solo ambiente. Y ninguna
 * intercepta el puntero, para que no estorben al formulario.
 */

import type { CSSProperties } from 'react';
import { cn } from '@/lib/utils';

type FiguraProps = { className?: string; style?: CSSProperties };

/** Huella de perro: almohadilla y cuatro dedos. */
export function Huella({ className, style }: FiguraProps) {
  return (
    <svg viewBox="0 0 64 64" fill="currentColor" style={style} className={cn('h-8 w-8', className)} aria-hidden>
      <ellipse cx="32" cy="41" rx="14" ry="12" />
      <ellipse cx="14.5" cy="25" rx="6.2" ry="8" transform="rotate(-18 14.5 25)" />
      <ellipse cx="26" cy="16.5" rx="5.8" ry="7.8" transform="rotate(-7 26 16.5)" />
      <ellipse cx="38" cy="16.5" rx="5.8" ry="7.8" transform="rotate(7 38 16.5)" />
      <ellipse cx="49.5" cy="25" rx="6.2" ry="8" transform="rotate(18 49.5 25)" />
    </svg>
  );
}

/**
 * El telón de fondo del hero: huellas repartidas y flotando.
 *
 * Las posiciones están puestas a mano para que ninguna quede detrás del texto
 * ni del bloque de tarjetas; por eso no es un bucle.
 */
export function TelonMascotas() {
  // pos = dónde va y de qué tamaño; rot = cuánto gira; tono = qué tan visible.
  // Van separados porque la animación de flotar ocupa el `transform` del
  // elemento: si el giro fuera una clase más, el keyframe lo borraría y todas
  // las figuras aparecerían derechas. Por eso gira el envoltorio y flota el
  // hijo.
  // Solo huellas: son la figura que representa al oficio sin competir con el
  // texto. Las siluetas de perro y gato, a este tamaño y opacidad, se leían
  // como manchas y le quitaban limpieza a la pantalla.
  //
  // pos = dónde va y de qué tamaño; rot = cuánto gira; tono = qué tan visible.
  // El giro va aparte porque la animación de flotar ocupa el `transform` del
  // elemento: si fuera una clase más, el keyframe lo borraría y saldrían todas
  // derechas. Por eso gira el envoltorio y flota el hijo.
  const flotantes: {
    pos: string; rot: string; tono: string; delay: string; dur: string;
  }[] = [
    { pos: 'left-[5%]   top-[13%]   h-11 w-11', rot: '-18deg', tono: 'text-white/[0.17]', delay: '0s',   dur: '7s' },
    { pos: 'left-[16%]  top-[22%]   h-7 w-7',   rot: '12deg',  tono: 'text-white/[0.13]', delay: '.9s',  dur: '8s' },
    { pos: 'left-[31%]  top-[7%]    h-8 w-8',   rot: '32deg',  tono: 'text-white/[0.12]', delay: '2s',   dur: '7.8s' },
    { pos: 'right-[30%] top-[11%]   h-6 w-6',   rot: '-8deg',  tono: 'text-white/[0.12]', delay: '2.2s', dur: '9s' },
    { pos: 'right-[9%]  top-[9%]    h-12 w-12', rot: '-14deg', tono: 'text-white/[0.16]', delay: '.4s',  dur: '9s' },
    { pos: 'right-[20%] top-[31%]   h-9 w-9',   rot: '24deg',  tono: 'text-white/[0.14]', delay: '1.6s', dur: '7.5s' },
    { pos: 'right-[7%]  top-[46%]   h-7 w-7',   rot: '8deg',   tono: 'text-white/[0.13]', delay: '1.8s', dur: '8.8s' },
    { pos: 'right-[14%] bottom-[16%] h-10 w-10', rot: '6deg',  tono: 'text-white/[0.15]', delay: '1.2s', dur: '8.5s' },
    { pos: 'right-[32%] bottom-[10%] h-6 w-6',  rot: '-22deg', tono: 'text-white/[0.12]', delay: '.6s',  dur: '8.2s' },
  ];

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      {flotantes.map(({ pos, rot, tono, delay, dur }, i) => (
        <span key={i} className={cn('absolute block', pos)} style={{ transform: `rotate(${rot})` }}>
          {/* Cada una flota a su propio ritmo: con la misma duración se mueven
              todas a la vez y el efecto se ve mecánico. */}
          <Huella
            className={cn('ge-float h-full w-full', tono)}
            style={{ animationDelay: delay, animationDuration: dur }}
          />
        </span>
      ))}
    </div>
  );
}
